import { ColDef, RowClickedEvent, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { useSAL2020_R01Store } from "./Store_SAL2020_R01";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";

import { SAL2020_R01HeaderDto } from "./Dto_SAL2020_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'SAL2020_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL2020_R01HeaderDto> | null>;
	defaultSAL2020_R01HeaderRow: SAL2020_R01HeaderDto;
	handleCreateClick: () => void;
}

export const Table_SAL2020_R01 = (props: Props) => {

	const { gridRef, defaultSAL2020_R01HeaderRow, handleCreateClick } = props;

	const { SAL2020_R01List, 
		selectedSAL2020_R01, setSelectedSAL2020_R01, initSelectedSAL2020_R01,
		columnState, setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
		editSAL2020_R01,
		SAL2020_R01DetailList, setSAL2020_R01DetailList,
	} = useSAL2020_R01Store();

	//const gridRef = useRef<AgGridReact>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'salBillNo',
		gridData: SAL2020_R01List,
		defaultGridData: defaultSAL2020_R01HeaderRow,
		selectedRow: selectedSAL2020_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		compNm: '사업장',
		salDt: '매출일자',
		customNm: '상호',
		salAmt: '매출금액',
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

	const baseColumnDefs: ColDef<SAL2020_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 119,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '매출일자', colId: 'salesDt', field: 'salesDt', width: 107,
			cellStyle: (p) => getCellStyle(p, 'salesDt'),
			headerClass: aggridHeaderClass,
			valueFormatter: (p: ValueFormatterParams<SAL2020_R01HeaderDto>) =>
				p.data?.salesDt ? formatDate(new Date(p.data.salesDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '상호', colId: 'customNm', field: 'customNm', width: 180,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '매출금액', colId: 'salesAmt', field: 'salesAmt', width: 118,
			cellStyle: (p) => getCellStyle(p, 'salesAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
	], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SAL2020_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SAL2020_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);


	// 마운트 시 컬럼 상태 복원
	
	const handleRowClick = useCallback((e: RowClickedEvent<SAL2020_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 전표정보와 같은지 확인
		const isSelected = selectedSAL2020_R01?.salBillNo === rowData.salBillNo;
		if (isSelected) {
			setDetailScrollTop(null);
			setSelectedSAL2020_R01(undefined);
			handleCreateClick();
		} else {
			setDetailScrollTop(null);
			setSelectedSAL2020_R01(rowData);
		}
	}, [selectedSAL2020_R01, setSelectedSAL2020_R01, setDetailScrollTop, handleCreateClick]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compNm: SAL2020_R01List.length.toLocaleString() + "건",
		salesAmt: SAL2020_R01List.reduce((sum, item) => (sum + item.salesAmt), 0).toLocaleString(),
		editable: false,
	}]), [SAL2020_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [SAL2020_R01List, selectedSAL2020_R01]);

	return (
		
		<GridContextWrapper<SAL2020_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedSAL2020_R01}
			defaultRowData={defaultSAL2020_R01HeaderRow}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SAL2020_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				
				rowClassRules={createRowClassRules<SAL2020_R01HeaderDto>({
					idKey: 'salBillNo',
					selectedList: selectedSAL2020_R01 ? [selectedSAL2020_R01.salBillNo] : [],
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}    

				// Grid_Select에 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 호버 상태 이벤트를 위한 저장
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setColumnState}				//컬럼 상태 저장
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	)
}
